import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  PreferencesService,
} from 'elior-group.joyfood.packages.order-validator';
import {
  BehaviorSubject,
  delay,
  distinctUntilChanged,
  finalize,
  Observable,
  tap,
} from 'rxjs';
import {
  AcceptTermsCommand,
  ChangePasswordCommand,
  CustomerDto,
  CustomersService,
} from 'src/api';
import { environment } from 'src/environments/environment';
import { LocalWalletService } from 'src/shared/services/local-wallet/local-wallet.service';
import { getDateAsString } from 'src/shared/util/date.util';
import { InAppNotificationService } from './in-app-notification.service';
import { ArticleRatingFavoriteService } from '../pages/orders/services/article-rating-favorite/article-rating-favorite.service';
import { TermsAndConditionsModel } from '../pages/profile/models/terms-and-conditions.model';
import { PasswordResetModel } from '../pages/root/reset-password/models/password-reset.model';
import { ResetPasswordRequestModel } from '../pages/root/reset-password/models/reset-password-request.model';

type CustomerPendencies = 'newpassword' | 'onboarding' | 'none';

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  private meSubject = new BehaviorSubject<CustomerDto | null>(
    this.getUserFromLocalStorage()
  );
  private loading = new BehaviorSubject<boolean>(true);
  private static readonly LOCAL_STORAGE_KEY = 'currentUser';
  private customerPendencies = new BehaviorSubject<CustomerPendencies>('none');
  pendenciesStatus$ = this.customerPendencies
    .asObservable()
    .pipe(distinctUntilChanged());
  me$ = this.meSubject.asObservable();
  loading$ = this.loading.asObservable();

  constructor(
    private preferencesService: PreferencesService,
    private customerApiService: CustomersService,
    private walletService: LocalWalletService,
    private articleRatingFavoriteService: ArticleRatingFavoriteService,
    private inAppNotificationService: InAppNotificationService,
    private http: HttpClient
  ) {
    const currentUser = this.customerDetails;

    if (currentUser) {
      this.checkCustomerPendencies(currentUser);
    }
  }

  get customerDetails() {
    return this.meSubject.value;
  }

  refreshCurrentUser$() {
    return this.syncCurrentUser$();
  }

  fetchCurrentUser$() {
    this.loading.next(true);

    return this.syncCurrentUser$().pipe(
      tap((me) => this.runBootstrapSideEffects(me)),
      delay(1500),
      finalize(() => {
        this.loading.next(false);
      })
    );
  }

  private syncCurrentUser$() {
    return this.customerApiService.apiCustomersMeGet().pipe(
      tap((me) => {
        this.applyCurrentUser(me);
      })
    );
  }

  private applyCurrentUser(user: CustomerDto) {
    this.meSubject.next(user);
    this.saveUserToLocalStorage(user);
    this.checkCustomerPendencies(user);
  }

  private runBootstrapSideEffects(user: CustomerDto) {
    if (user.onboarded) {
      this.preferencesService.loadUserPreferences();
    }

    this.preferencesService.loadAvailablePreferences();
    this.articleRatingFavoriteService.fetchCustomerFavorites();
    this.articleRatingFavoriteService.fetchCustomerRatings();
    this.walletService.fetchBalance$().subscribe();
    this.inAppNotificationService.startPolling();
  }

  private saveUserToLocalStorage(user: CustomerDto) {
    const date = new Date();
    date.setHours(date.getHours() + 24);

    localStorage.setItem(
      CustomerService.LOCAL_STORAGE_KEY,
      JSON.stringify({
        user,
        expirationDate: date.toISOString(),
      })
    );
  }

  private getUserFromLocalStorage(): CustomerDto | null {
    const data = localStorage.getItem(CustomerService.LOCAL_STORAGE_KEY);
    if (data) {
      const parsedData = JSON.parse(data);
      const expirationDate = new Date(parsedData.expirationDate);
      if (expirationDate > new Date()) {
        return parsedData.user;
      } else {
        localStorage.removeItem(CustomerService.LOCAL_STORAGE_KEY);
      }
    }
    return null;
  }

  checkCustomerPendencies(customer: CustomerDto) {
    const { lastPasswordChange, onboarded } = customer;

    switch (true) {
      case !lastPasswordChange:
        this.customerPendencies.next('newpassword');
        break;

      case !onboarded:
        this.customerPendencies.next('onboarding');
        break;

      default:
        this.customerPendencies.next('none');
        break;
    }
  }

  saveNewPassword(passwordChangeModel: ChangePasswordCommand): Observable<any> {
    return this.customerApiService.apiCustomersNewPasswordPut(
      passwordChangeModel
    );
  }

  deleteAccount() {
    return this.customerApiService.apiCustomersDelete();
  }

  sendResetPasswordRequest(payload: ResetPasswordRequestModel | null) {
    return this.http.post(
      `${environment.apiConfig.webApiUri}/api/customers/reset-password-request`,
      payload
    );
  }

  sendResetPasswordValidation(payload: PasswordResetModel | null) {
    return this.http.post(
      `${environment.apiConfig.webApiUri}/api/customers/reset-password-validation`,
      payload
    );
  }

  setPasswordResetDate() {
    const user = this.getUserFromLocalStorage();
    user!.lastPasswordChange = getDateAsString(new Date());
    this.saveUserToLocalStorage(user!);
    this.meSubject.next(user);
  }

  setOnboarded() {
    const user = this.getUserFromLocalStorage();
    user!.onboarded = true;
    this.saveUserToLocalStorage(user!);
    this.meSubject.next(user);
  }

  acceptTerms(
    termsAndConditionsModel: TermsAndConditionsModel | null
  ): Observable<any> {
    const command: AcceptTermsCommand = {
      azureAdUpn: '',
      acceptedMarketing: termsAndConditionsModel?.acceptedMarketing ?? false,
    };

    return this.customerApiService.apiCustomersAcceptTermsPut(command);
  }
}
